import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs, type Crumb } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ExerciseRenderer } from '@/components/exercises/renderers/ExerciseRenderer'
import { exerciseRunsApi, type ExerciseRun } from '@/api/exercises'
import { useExerciseRunRealtime } from '@/realtime/useExerciseRunRealtime'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'

/**
 * Откуда открыли попытку: урок, пункт домашки или тема программы.
 *
 * Тип описан здесь, а не в модуле API: блок `origin` в ресурсе появляется
 * отдельной правкой, и до неё поля просто нет — читаем его защитно, чтобы
 * страница пережила и старый ответ сервера.
 */
type RunOrigin = {
  /** Дата урока приходит как `planned_start_at` — отдельного поля `date` в ресурсе нет */
  lesson?: { id?: number | null; planned_start_at?: string | null; title?: string | null } | null
  homework?: { id?: number | null; item_id?: number | null } | null
  theme?: { assignment_id?: number | null; assigned_theme_id?: number | null; title?: string | null } | null
}

/** Непустая строка или null — пустой заголовок с сервера ставить в крошку незачем */
function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/** Идентификатор или null: приходить может и строка, и мусор — тогда крошки нет */
function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Подпись крошки из частей, которых может не быть.
 *
 * Пустая склейка — не подпись: крошка схлопывается в невидимую ссылку, по
 * которой некуда попасть глазами. Когда сервер не дал ни одной части, берём
 * запасной текст с идентификатором — он хотя бы называет объект.
 */
function crumbLabel(parts: Array<string | null>, fallback: string): string {
  const label = parts.filter((part): part is string => part !== null).join(' · ')

  return label === '' ? fallback : label
}

/**
 * Экран решения задания: единый для всех типов, содержимое рисует рендерер
 * своего типа.
 *
 * Ответ отправляется целиком одной сдачей: промежуточных сохранений нет —
 * иначе «сданным» пришлось бы считать любое случайное касание.
 */
export function ExerciseRunPage() {
  const { id } = useParams<{ id: string }>()
  const runId = Number(id)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const authUserId = useAuthStore((s) => s.user?.id ?? null)

  const [run, setRun] = useState<ExerciseRun | null>(null)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Номер запроса: при недоступном Reverb страница опрашивает сервер каждые
  // несколько секунд, и медленный ответ такого опроса приходил уже после сдачи
  // — он возвращал экран к форме решения с кнопкой «Сдать», и ученик сдавал
  // работу второй раз.
  const requestRef = useRef(0)

  const load = useCallback(async (keepAnswers = false) => {
    const request = ++requestRef.current
    // Скелетон только на первой загрузке: фоновое обновление не должно
    // размонтировать рендерер — вместе с ним теряется локальное состояние,
    // а у устного ответа обрывается запись микрофона.
    if (!keepAnswers) setLoading(true)
    setError(null)
    try {
      const loaded = await exerciseRunsApi.get(runId)

      // Пока ответ летел, состояние успело смениться более свежим запросом
      // или сдачей — устаревшее его перетирать не должно
      if (request !== requestRef.current) return

      setRun(loaded)

      // Черновик ученика не трогаем при обновлении: промежуточных сохранений
      // нет, у активной попытки `answers` пусты, и перезапись стирала бы всё
      // набранное — при недоступном WebSocket это происходило бы каждые
      // несколько секунд.
      if (!keepAnswers || loaded.status !== 'active') {
        setAnswers(loaded.answers ?? {})
      }
    } catch (err) {
      if (request === requestRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [runId, t])

  useEffect(() => {
    if (Number.isFinite(runId)) {
      void load()

      return
    }

    setLoading(false)
    setError(t('common.error'))
  }, [load, runId, t])

  // Учитель мог снять задание или проверить работу, пока страница открыта
  useExerciseRunRealtime(Number.isFinite(runId) ? runId : null, () => void load(true))

  const submit = async () => {
    if (!run) return
    // Уже летящий опрос обесцениваем сразу: его ответ описывает попытку до
    // сдачи и вернул бы статус `active` поверх результата
    requestRef.current += 1
    setSubmitting(true)
    setError(null)
    try {
      const submitted = await exerciseRunsApi.submit(run.id, answers)

      // Результат сдачи побеждает безусловно. При недоступном Reverb страница
      // опрашивает сервер каждые несколько секунд, и опрос, ушедший уже во
      // время сдачи, раньше отменял её результат: экран возвращался к форме,
      // ученик сдавал второй раз и получал ошибку. Инкремент после ответа
      // гасит все летящие GET: они старше сдачи в любом случае
      requestRef.current += 1
      setRun(submitted)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const exercise = run?.exercise
  const isActive = run?.status === 'active'
  const scoreLabel = run?.score_percent === null || run?.score_percent === undefined ? null : `${run.score_percent}%`

  /**
   * Крошки и осмысленный возврат: с урока — в урок, из домашки — в домашку, с
   * темы — в программу. `navigate(-1)` оставлен только там, где вести некуда:
   * в самопрактику страница открывается из разных мест.
   */
  const { crumbs, backTo } = useMemo<{ crumbs: Crumb[]; backTo: string | null }>(() => {
    const current: Crumb = { label: t('exercises.run.title') }
    const origin = (run as (ExerciseRun & { origin?: RunOrigin | null }) | null)?.origin ?? null

    if (run === null || origin === null || typeof origin !== 'object') {
      return { crumbs: [current], backTo: null }
    }

    // Учитель открывает ту же страницу, но маршруты ученика ему закрыты —
    // ссылку выбираем по тому, чья это работа
    const isOwnRun = authUserId !== null && run.student_id === authUserId

    const lessonId = num(origin.lesson?.id)
    if (lessonId !== null) {
      const plannedAt = text(origin.lesson?.planned_start_at)
      const label = crumbLabel(
        [plannedAt === null ? null : formatDateDMY(plannedAt), text(origin.lesson?.title)],
        t('lessons.detailsTitle', { id: lessonId }),
      )
      const to = ROUTES.LESSON_DETAILS(lessonId)

      return {
        crumbs: [{ label: t('lessons.title'), to: ROUTES.LESSONS }, { label, to }, current],
        backTo: to,
      }
    }

    if (num(origin.homework?.id) !== null) {
      // Отдельной страницы одной домашки нет: у ученика это общий список, у
      // учителя — список домашек этого ученика
      const to = isOwnRun ? ROUTES.HOMEWORKS : ROUTES.USER_HOMEWORKS(run.student_id)

      return { crumbs: [{ label: t('homeworks.title'), to }, current], backTo: to }
    }

    const assignmentId = num(origin.theme?.assignment_id)
    if (assignmentId !== null) {
      // Раздел первым уровнем — как у урока и домашки: без него тема стояла на
      // месте раздела и путь начинался с «Главная › Present Perfect».
      // Ученику это его программы, учителю тот же маршрут закрыт — ведём в
      // список программ ученика, чью работу он открыл.
      const section: Crumb = isOwnRun
        ? { label: t('programs.my.title', 'Моя программа'), to: ROUTES.MY_PROGRAMS }
        : { label: t('programs.title', 'Программы обучения'), to: ROUTES.USER_PROGRAMS(run.student_id) }
      const to = isOwnRun
        ? ROUTES.MY_PROGRAM_VIEW(assignmentId)
        : ROUTES.USER_PROGRAM_ASSIGNMENT(run.student_id, assignmentId)
      // Запасной текст свой, а не название раздела: одинаковые подписи двух
      // соседних крошек читаются как повтор пути
      const label = crumbLabel(
        [text(origin.theme?.title)],
        t('exercises.run.origin.theme', 'Тема программы'),
      )

      return {
        crumbs: [section, { label, to }, current],
        backTo: to,
      }
    }

    return { crumbs: [current], backTo: null }
  }, [run, authUserId, t])

  return (
    <DashboardLayout>
      {/* Те же отступы, что на соседних страницах: без обёртки контент
          прижимался к краю, и переход со списка дёргал вёрстку */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={crumbs} />
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => (backTo === null ? navigate(-1) : navigate(backTo))}>
            {t('common.back')}
          </Button>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flex: 1 }}>
            {exercise?.title ?? t('exercises.run.title')}
          </Typography>
          <PageHelpPopover pageKey="exercise-run" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && <Skeleton variant="rounded" height={360} />}

        {!loading && run && exercise && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
              <Chip size="small" label={t(`exercises.status.${run.status}`)} color={isActive ? 'primary' : 'default'} />
              {run.mode === 'training' && <Chip size="small" label={t('exercises.run.training')} />}
              {scoreLabel && <Chip size="small" color="success" label={scoreLabel} />}
              <Chip size="small" variant="outlined" label={t('exercises.run.attempt', { number: run.attempt_number })} />
            </Stack>

            {exercise.instructions && (
              <Alert severity="info" icon={false}>
                {exercise.instructions}
              </Alert>
            )}

            {run.status === 'teacher_review' && <Alert severity="info">{t('exercises.run.awaitingTeacher')}</Alert>}
            {run.status === 'cancelled' && <Alert severity="warning">{t('exercises.run.cancelled')}</Alert>}

            <Card>
              <CardContent>
                <ExerciseRenderer
                  type={exercise.type}
                  content={exercise.content}
                  answers={answers}
                  onChange={setAnswers}
                  readOnly={!isActive}
                  runId={run.id}
                  // Попытка уже загружена здесь со всеми полями: без неё рендерер
                  // диктанта тянул бы ту же работу вторым запросом и до его
                  // ответа показывал бы ученику непотраченный лимит
                  run={run}
                  delegateId={run.delegate?.id ?? null}
                  delegateStatus={run.delegate?.status ?? null}
                />
              </CardContent>
            </Card>

            {run.teacher_comment && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('exercises.run.teacherComment')}
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{run.teacher_comment}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Не `=== null`: у обычного задания ресурс поле delegate вовсе не
                присылает, и строгое сравнение прятало бы кнопку сдачи всегда */}
            {isActive && !run.delegate && (
              <>
                <Divider />
                <Stack direction="row" justifyContent="flex-end">
                  <Button variant="contained" disabled={submitting} onClick={() => void submit()}>
                    {submitting ? t('common.loading') : t('exercises.run.submit')}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Box>
    </DashboardLayout>
  )
}
